import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { auth } from '../config/firebase';
import { signInWithPopup, signOut, GoogleAuthProvider, OAuthProvider, updateProfile, browserLocalPersistence, setPersistence, onAuthStateChanged, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { createUserProfile } from '../services/databaseService';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) return;

    console.log('🚀 Starting auth flow');
    
    // Keep the loading state true until we get the first auth state update
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      console.log('📝 Auth state changed:', !!authUser);
      
      if (authUser) {
        try {
          await createUserProfile(authUser);
        } catch (error) {
          console.error('Error creating user profile:', error);
        }
      }

      setUser(authUser);
      setLoading(false);
    });
    
    // Handle redirect result after setting up the listener
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          await createUserProfile(result.user);
          sessionStorage.removeItem('googleAuthInProgress');
        }
      })
      .catch(console.error);

    isInitializedRef.current = true;
    return () => {
      unsubscribe();
      isInitializedRef.current = false;
    };
  }, []);

  const isRunningInWebView = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.includes('snapchat') ||
      userAgent.includes('instagram') ||
      userAgent.includes('linkedin') ||
      userAgent.includes('facebook') ||
      userAgent.includes('wv') // Generic WebView
    );
  };

  const signInWithGoogle = async () => {
    if (isRunningInWebView()) {
      // Show modal/toast asking user to open in browser
      alert('För bästa upplevelse, öppna länken i din webbläsare istället');
      
      // Optionally, try to open in default browser
      window.location.href = `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.android.chrome;end`;
      
      // For iOS
      window.location.href = `googlechrome://${window.location.host}${window.location.pathname}`;
      
      return;
    }
    
    if (isAuthenticating) return;

    try {
      setIsAuthenticating(true);
      await setPersistence(auth, browserLocalPersistence);

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      if (isMobile()) {
        sessionStorage.setItem('googleAuthInProgress', 'true');
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider);
        await createUserProfile(result.user);
      }

    } catch (error) {
      console.error('Error signing in with Google:', error);
      sessionStorage.removeItem('googleAuthInProgress');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const generateRandomState = () => {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0].toString(36);
  };

  const signInWithApple = async () => {
    if (isAuthenticating) return;
    try {
      setIsAuthenticating(true);
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const displayName = user.displayName || 'Anonymous User';
      
      if (!user.displayName || user.photoURL === '/images/default-avatar.png') {
        const updateData = {
          displayName,
          photoURL: user.displayName ? null : 'question-circle'
        };
        
        await updateProfile(user, updateData);
        await createUserProfile({
          uid: user.uid,
          email: user.email,
          displayName,
          photoURL: updateData.photoURL
        });
      } else {
        await createUserProfile({
          uid: user.uid,
          email: user.email,
          displayName,
          photoURL: user.photoURL
        });
      }

    } catch (error) {
      console.error('Error signing in with Apple:', error);
      if (error.code === 'auth/cancelled-popup-request') {
        console.log('Sign-in popup was closed by the user');
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // Clear any auth-related storage
      sessionStorage.removeItem('googleAuthInProgress');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    signInWithGoogle,
    signInWithApple,
    logout,
    loading,
    isAuthenticating
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

//Changes:**
//   The listener is only here and we aren't waiting for the auth state.
//  Removed the `isAuthInitializedRef` in favor of `useRef` that returns a function.